import React from 'react';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Autocomplete, {
  autocompleteClasses,
  AutocompleteProps,
  AutocompleteValue,
} from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash/debounce';
import Input from './Input';
import { NATION_WIDE } from '@/constants/city';

export interface MyAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends Omit<
    AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    'onChange' | 'options' | 'renderInput'
  > {
  value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;
  onChange: (
    value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
  ) => void;
  placeholder?: string;
  label?: string;
  labelColor?: string;
  openAtFirstClick?: boolean;
  getOptions: (query: string) => Promise<{ data: any[] }>;
  inputSx?: React.CSSProperties | Record<string, React.CSSProperties>;
  error?: boolean;
  helperText?: string;
  endAdornment?: React.ReactNode;
  subLabel?: string;
  isSearchCity?: boolean;
  handleMyInputChange?: () => void;
}

const MyAutocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: MyAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
) => {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly T[]>([]);
  const [inputValue, setInputValue] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const {
    value = undefined,
    onChange,
    getOptions,
    getOptionLabel,
    freeSolo,
    placeholder = '',
    label = '',
    labelColor,
    className = '',
    openAtFirstClick = true,
    noOptionsText = '抱歉，暂未搜索到相关数据',
    sx,
    inputSx = {},
    disabled = false,
    error = false,
    helperText = '',
    subLabel,
    filterOptions = (options) => options,
    endAdornment = <SearchIcon sx={{ color: '#79747e' }} />,
    isSearchCity = false,
    handleMyInputChange,
    ...otherProps
  } = props;

  const styledPopper = styled(Popper)({
    paddingTop: '4px',
    [`.${autocompleteClasses.listbox} .${autocompleteClasses.option}.${autocompleteClasses.focused}`]:
      {
        color: '#1861bf',
        background: '#eef1fe',
        borderRadius: '8px',
      },
  });

  const handleChange = (
    event: React.SyntheticEvent,
    value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
  ) => {
    onChange && onChange(value);
  };

  const handleInputChange = (
    event: React.SyntheticEvent,
    inputValue: string,
  ) => {
    if (!openAtFirstClick && event) {
      setOpen(!!inputValue.trim());
    }
    setInputValue(inputValue);
    handleMyInputChange && handleMyInputChange();
    if (freeSolo && event && onChange) {
      onChange(
        inputValue as AutocompleteValue<
          T,
          Multiple,
          DisableClearable,
          FreeSolo
        >,
      );
    }
  };

  const handleSearch = React.useCallback(
    debounce(async (query, active) => {
      const response = await getOptions(query.trim());
      if (active) {
        setLoading(false);
        const data = response.data;
        if (Array.isArray(data) && isSearchCity) {
          data.unshift(NATION_WIDE as any);
          setOptions(data);
        } else {
          setOptions(data);
        }
      }
    }, 300),
    [],
  );

  React.useEffect(() => {
    let active = true;

    if (!open) {
      setOptions([]);
      return;
    }
    setOptions(() => {
      setLoading(true);
      return [];
    });
    handleSearch(inputValue, active);

    return () => {
      active = false;
    };
  }, [open, inputValue]);

  return (
    <div className={className}>
      {(label || subLabel) && (
        <div className="mb-2 tablet:mb-3 flex flex-col">
          {label && (
            <label
              className={`font-semibold ${labelColor} leading-5.5 text-sm`}
            >
              {label}
            </label>
          )}
          {subLabel && (
            <label className="text-xs mt-1 text-gray-600">{subLabel}</label>
          )}
        </div>
      )}
      <Autocomplete<T, Multiple, DisableClearable, FreeSolo>
        freeSolo={freeSolo}
        disabled={disabled}
        blurOnSelect={true}
        options={options}
        value={value}
        inputValue={inputValue}
        open={open}
        noOptionsText={noOptionsText}
        loading={freeSolo ? false : loading}
        loadingText={<CircularProgress color="inherit" size={24} />}
        onOpen={() => openAtFirstClick && setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        PopperComponent={styledPopper}
        renderInput={(params) => {
          return (
            <Input
              autoComplete="off"
              sx={inputSx}
              error={error}
              inputRef={params.InputProps.ref}
              helperText={helperText}
              placeholder={placeholder}
              inputProps={params.inputProps}
              disabled={disabled}
              endAdornment={endAdornment}
            />
          );
        }}
        {...otherProps}
      />
    </div>
  );
};

export default MyAutocomplete;
